<template>
  <div>
    <h4>Solicitar Documentos</h4>
    <p style="margin-top: 0; color: #757575; font-size: 0.875rem">
      O orçamento aguarda documentos antes de gerar as AETs. Anexe cada
      pendência abaixo (ART do transportador e/ou AET vigente do DNIT para os
      cavalos do DERPE). Quando tudo for enviado, o orçamento avança
      automaticamente para Solicitar AET.
    </p>

    <div v-if="!stateReady" class="p-text-center" style="padding: 2rem">
      <ProgressSpinner style="width: 36px; height: 36px" />
    </div>

    <div v-else>
      <!-- Card ART -->
      <div v-if="pendencias && pendencias.precisaArt" class="card-doc">
        <div class="card-doc__header">
          <i class="pi pi-id-card" style="margin-right: 0.5rem" />
          <strong>ART do transportador</strong>
        </div>
        <p class="card-doc__hint">
          Nenhuma ART vigente foi encontrada para o transportador. Faça upload
          do PDF e informe número e validade.
        </p>
        <form @submit.prevent="submitUploadArt">
          <fieldset :disabled="stateLoading">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-4">
                <input-with-label
                  label="Número da ART"
                  :required="true"
                  v-slot="l"
                >
                  <InputText
                    v-model="formArt.art"
                    :id="l.id"
                    :required="l.required"
                    maxlength="30"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-4">
                <input-with-label label="Validade" :required="true" v-slot="l">
                  <Calendar
                    v-model="formArt.dataVencimento"
                    :id="l.id"
                    :required="l.required"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12">
                <input-file
                  label="Arquivo da ART (PDF)"
                  subLabel="máx 5MB"
                  :id="stateId + '_art'"
                  :hasDownload="false"
                  accept="application/pdf"
                />
              </div>
              <div class="p-col-12">
                <Button
                  type="submit"
                  class="p-button-rounded"
                  label="Enviar ART"
                  icon="pi pi-upload"
                />
              </div>
            </div>
          </fieldset>
        </form>
      </div>

      <div
        v-for="(pendencia, idx) in pendenciasAetDnit"
        :key="'dnit-' + pendencia.idLicencaMae"
        class="card-doc"
      >
        <div class="card-doc__header">
          <i class="pi pi-file-pdf" style="margin-right: 0.5rem" />
          <strong>
            AET DNIT do cavalo {{ pendencia.placaCavalo || "—" }}
          </strong>
        </div>
        <p class="card-doc__hint">
          O DERPE exige AET vigente do DNIT como anexo. Não encontramos nenhuma
          AET do DNIT para este cavalo (licença origem #{{
            pendencia.idLicencaMae
          }}). Anexe o PDF, informe número e validade.
        </p>
        <form @submit.prevent="submitUploadDnit(pendencia, idx)">
          <fieldset :disabled="stateLoading">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label
                  label="Número da AET DNIT"
                  :required="true"
                  v-slot="l"
                >
                  <InputText
                    v-model="formsDnit[idx].numeroAet"
                    :id="l.id"
                    :required="l.required"
                    maxlength="50"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label
                  label="Início da validade"
                  :required="true"
                  v-slot="l"
                >
                  <Calendar
                    v-model="formsDnit[idx].dtInicial"
                    :id="l.id"
                    :required="l.required"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label
                  label="Fim da validade"
                  :required="true"
                  v-slot="l"
                >
                  <Calendar
                    v-model="formsDnit[idx].dtFinal"
                    :id="l.id"
                    :required="l.required"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12">
                <input-file
                  label="Arquivo da AET DNIT (PDF)"
                  subLabel="máx 5MB"
                  :id="stateId + '_dnit_' + idx"
                  :hasDownload="false"
                  accept="application/pdf"
                />
              </div>
              <div class="p-col-12">
                <Button
                  type="submit"
                  class="p-button-rounded"
                  label="Enviar AET DNIT"
                  icon="pi pi-upload"
                />
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref,
  PropType
} from "vue";
import { useToast } from "../../modules/message";
import { useStorage } from "../../modules/storage";
import {
  useOrcamentoService,
  PendenciasRenovacaoPayload,
  PendenciaAetDnitPayload
} from "../../modules/business/orcamento/orcamentoBusiness";

interface FormArt {
  art: string;
  dataVencimento: Date | null;
}

interface FormDnit {
  numeroAet: string;
  dtInicial: Date | null;
  dtFinal: Date | null;
}

interface UploadDocsState {
  stateReady: boolean;
  pendencias: PendenciasRenovacaoPayload | null;
  formArt: FormArt;
  formsDnit: Array<FormDnit>;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<UploadDocsState>({
      stateReady: false,
      pendencias: null,
      formArt: { art: "", dataVencimento: null },
      formsDnit: [],
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const pendenciasAetDnit = computed<Array<PendenciaAetDnitPayload>>(
      () => state.pendencias?.pendenciasAetDnit ?? []
    );

    const carregarPendencias = (): Promise<void> => {
      const { getPendenciasRenovacao, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      return getPendenciasRenovacao(props.idOrcamento)
        .then(() => {
          state.pendencias = data.value as PendenciasRenovacaoPayload;
          state.formsDnit = (
            state.pendencias.pendenciasAetDnit ?? []
          ).map(() => ({ numeroAet: "", dtInicial: null, dtFinal: null }));
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const fazerUploadTemp = (
      inputId: string,
      msgErro: string
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const fileInput = document.querySelector(
          "#" + inputId
        ) as HTMLInputElement;
        if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
          reject(new Error(msgErro));
          return;
        }
        const formData = new FormData();
        formData.append("file", fileInput.files[0]);
        const storage = useStorage();
        state.loadings.push(storage.loading);
        storage
          .uploadTempFile(formData)
          .then((key: string) => {
            state.loadings.splice(state.loadings.indexOf(storage.loading), 1);
            resolve(key);
          })
          .catch((err) => {
            state.loadings.splice(state.loadings.indexOf(storage.loading), 1);
            reject(err);
          });
      });
    };

    const submitUploadArt = () => {
      if (!state.formArt.art || !state.formArt.dataVencimento) {
        useToast().warning("Preencha número e validade da ART");
        return;
      }
      fazerUploadTemp(state.stateId + "_art", "Selecione o arquivo da ART")
        .then((key) => {
          const { uploadArtRenovacao, loading, data } = useOrcamentoService();
          state.loadings.push(loading);
          uploadArtRenovacao(props.idOrcamento, {
            keyBlDocumentoTemp: key,
            art: state.formArt.art,
            dataVencimento: state.formArt.dataVencimento as Date
          })
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success("ART enviada com sucesso");
              emit("after-upload", data.value);
              carregarPendencias();
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        })
        .catch((err) => {
          useToast().warning(err?.message ?? "Falha no upload do arquivo");
        });
    };

    const submitUploadDnit = (
      pendencia: PendenciaAetDnitPayload,
      idx: number
    ) => {
      const form = state.formsDnit[idx];
      if (!form.numeroAet || !form.dtInicial || !form.dtFinal) {
        useToast().warning(
          "Preencha número, início e fim da validade da AET DNIT"
        );
        return;
      }
      fazerUploadTemp(
        state.stateId + "_dnit_" + idx,
        "Selecione o arquivo da AET DNIT"
      )
        .then((key) => {
          const {
            uploadAetDnitRenovacao,
            loading,
            data
          } = useOrcamentoService();
          state.loadings.push(loading);
          uploadAetDnitRenovacao(props.idOrcamento, {
            idLicencaMae: pendencia.idLicencaMae,
            keyBlDocumentoTemp: key,
            numeroAet: form.numeroAet,
            dtInicial: form.dtInicial as Date,
            dtFinal: form.dtFinal as Date
          })
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success(
                `AET DNIT do cavalo ${pendencia.placaCavalo ?? ""} enviada`
              );
              emit("after-upload", data.value);
              carregarPendencias();
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        })
        .catch((err) => {
          useToast().warning(err?.message ?? "Falha no upload do arquivo");
        });
    };

    onMounted(() => {
      carregarPendencias();
    });

    return {
      stateLoading,
      pendenciasAetDnit,
      submitUploadArt,
      submitUploadDnit,
      ...toRefs(state)
    };
  },
  props: {
    idOrcamento: {
      type: Number,
      required: true
    },
    parentLoading: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  }
});
</script>

<style scoped lang="scss">
.card-doc {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;

  &__header {
    font-size: 1rem;
    color: #424242;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
  }

  &__hint {
    color: #757575;
    font-size: 0.875rem;
    margin: 0 0 0.75rem 0;
  }
}
</style>
